import React, { useState, useMemo, useRef } from 'react';
import {
  Container,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  InsertDriveFile,
  Image,
  PictureAsPdf,
  VideoLibrary,
  Description,
  TableChart,
  GetApp,
  Delete,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useFiles } from '../context/FileContext';
import Notifications from './Notifications';
import '../styles/styles.css';

const WORKER_URL = 'https://storify-cors.storifyapp.site';

const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const FileManager = () => {
  const { files, loading, deleteFile, downloadFile } = useFiles();
  const [selectedFile, setSelectedFile] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [storage, setStorage] = useState(null);
  const notificationsRef = useRef();

  React.useEffect(() => {
    fetch(`${WORKER_URL}/storage`)
      .then((res) => res.json())
      .then((data) => setStorage(data))
      .catch(() => {});
  }, []);

  const usedPercent = storage ? Math.min((storage.used / storage.limit) * 100, 100) : 0;

  const getFileIcon = (fileType = '') => {
    const type = fileType.toLowerCase();
    if (type.includes('image')) return <Image sx={{ color: '#6C63FF' }} />;
    if (type.includes('pdf')) return <PictureAsPdf sx={{ color: '#e53935' }} />;
    if (type.includes('video')) return <VideoLibrary sx={{ color: '#9c27b0' }} />;
    if (type.includes('word') || type.includes('document')) return <Description sx={{ color: '#1565c0' }} />;
    if (type.includes('sheet') || type.includes('excel') || type.includes('csv')) return <TableChart sx={{ color: '#2e7d32' }} />;
    return <InsertDriveFile sx={{ color: '#6C63FF' }} />;
  };

  const filteredFiles = useMemo(() => {
    return files.filter(
      (file) =>
        file.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        file.path.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [files, searchTerm]);

  const handleMenuClick = (event, file) => {
    setAnchorEl(event.currentTarget);
    setSelectedFile(file);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const handleDelete = () => {
    setOpenDeleteDialog(true);
    handleMenuClose();
  };

  const confirmDelete = async () => {
    if (!selectedFile) return;
    const result = await deleteFile(selectedFile.id);
    if (result.success) {
      setOpenDeleteDialog(false);
      notificationsRef.current?.showSnackbar('File deleted successfully.', 'success');
      fetch(`${WORKER_URL}/storage`).then((res) => res.json()).then((data) => setStorage(data)).catch(() => {});
    } else {
      notificationsRef.current?.showSnackbar(result.error, 'error');
    }
  };

  const handleDownload = async () => {
    if (!selectedFile) return;
    const result = await downloadFile(selectedFile);
    if (result.success) {
      notificationsRef.current?.showSnackbar('Download started.', 'success');
    } else {
      notificationsRef.current?.showSnackbar(result.error, 'error');
    }
    handleMenuClose();
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const truncateName = (name, maxLen = 40) => {
    if (!name || name.length <= maxLen) return name;
    const ext = name.includes('.') ? '.' + name.split('.').pop() : '';
    const base = name.slice(0, maxLen - ext.length - 3);
    return `${base}...${ext}`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress sx={{ color: '#6C63FF' }} />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ pt: '100px', pb: '80px', userSelect: 'none' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography sx={{ fontSize: '1.8rem', fontWeight: 800, color: '#6C63FF', fontFamily: 'Poppins, sans-serif' }}>
          Files
        </Typography>
        <Link to="/upload" style={{ textDecoration: 'none' }}>
          <Button variant="contained" startIcon={<AddIcon />} sx={{
            backgroundColor: '#6C63FF', borderRadius: '10px', textTransform: 'none',
            fontWeight: 600, px: 2.5, '&:hover': { backgroundColor: '#4B45B3' },
          }}>
            Upload New File
          </Button>
        </Link>
      </Box>

      {storage && (
        <Box sx={{
          background: '#ffffff', borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(108,99,255,0.08)', p: '16px 20px', mb: 3,
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#444' }}>Storage used</Typography>
            <Typography variant="caption" sx={{ color: '#718096' }}>
              {formatBytes(storage.used)} / {formatBytes(storage.limit)}
            </Typography>
          </Box>
          <Box sx={{ position: 'relative', height: 8, borderRadius: 4, backgroundColor: 'rgba(108,99,255,0.12)', overflow: 'hidden' }}>
            {usedPercent > 0 && (
              <Box sx={{
                position: 'absolute', left: 0, top: 0, bottom: 0,
                width: `${usedPercent}%`, borderRadius: 4,
                backgroundColor: usedPercent > 85 ? '#e53935' : '#6C63FF',
                transition: 'width 0.4s ease',
              }} />
            )}
          </Box>
          <Typography variant="caption" sx={{ color: '#718096', mt: 0.5, display: 'block' }}>
            {formatBytes(storage.available)} available
          </Typography>
        </Box>
      )}

      <Box sx={{ background: '#ffffff', borderRadius: '16px', boxShadow: '0 4px 20px rgba(108,99,255,0.08)', overflow: 'hidden' }}>
        <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, borderBottom: '1px solid rgba(108,99,255,0.08)' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a2e' }}>Shared Files</Typography>
          <TextField
            size="small"
            placeholder="Search files..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#aaa' }} /></InputAdornment>,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px', fontSize: '0.875rem',
                '& fieldset': { borderColor: 'rgba(108,99,255,0.2)' },
                '&:hover fieldset': { borderColor: '#6C63FF' },
                '&.Mui-focused fieldset': { borderColor: '#6C63FF' },
              },
            }}
          />
        </Box>

        {filteredFiles.length > 0 ? (
          <List disablePadding>
            {filteredFiles.map((file, index) => (
              <React.Fragment key={file.id}>
                <ListItem sx={{ px: 3, py: 1.5, '&:hover': { background: 'rgba(108,99,255,0.04)' } }}>
                  <ListItemIcon sx={{ minWidth: 44, flexShrink: 0 }}>
                    <Box sx={{
                      width: 36, height: 36, borderRadius: '10px',
                      background: 'rgba(108,99,255,0.08)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {getFileIcon(file.type)}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    sx={{ minWidth: 0, mr: 1 }}
                    primary={
                      <Typography sx={{
                        fontWeight: 600, fontSize: '0.875rem', color: '#1a1a2e',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        maxWidth: '100%',
                      }}>
                        {truncateName(file.name)}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" sx={{ color: '#718096', display: 'block' }}>
                        {file.path} • {formatBytes(file.size)} • Expires {formatDate(file.expires_at)}
                      </Typography>
                    }
                  />
                  <IconButton size="small" onClick={(e) => handleMenuClick(e, file)} sx={{ color: '#aaa', flexShrink: 0 }}>
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </ListItem>
                {index < filteredFiles.length - 1 && <Divider sx={{ mx: 3, borderColor: 'rgba(108,99,255,0.06)' }} />}
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
            <InsertDriveFile sx={{ fontSize: 48, color: 'rgba(108,99,255,0.2)', mb: 1 }} />
            <Typography variant="h6" sx={{ color: '#718096', mb: 0.5 }}>
              {searchTerm ? 'No files found.' : 'Nobody has uploaded any files yet.'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#aaa', mb: 2 }}>
              {searchTerm ? 'Try a different search term.' : 'Start by uploading your first file.'}
            </Typography>
            {!searchTerm && (
              <Link to="/upload" style={{ textDecoration: 'none' }}>
                <Button variant="outlined" startIcon={<AddIcon />} sx={{
                  borderRadius: '10px', textTransform: 'none', fontWeight: 600,
                  borderColor: '#6C63FF', color: '#6C63FF',
                  '&:hover': { backgroundColor: 'rgba(108,99,255,0.06)' },
                }}>
                  Upload File
                </Button>
              </Link>
            )}
          </Box>
        )}
      </Box>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}
        PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.1)' } }}>
        <MenuItem onClick={handleDownload} sx={{ gap: 1, fontSize: '0.875rem' }}>
          <GetApp fontSize="small" sx={{ color: '#6C63FF' }} /> Download
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ gap: 1, fontSize: '0.875rem', color: '#e53935' }}>
          <Delete fontSize="small" /> Delete
        </MenuItem>
      </Menu>

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}
        PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete File</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Are you sure you want to delete "{truncateName(selectedFile?.name, 30)}"?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} sx={{ textTransform: 'none', color: '#718096' }}>Cancel</Button>
          <Button onClick={confirmDelete} variant="contained" color="error" sx={{ textTransform: 'none', borderRadius: '8px' }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Notifications ref={notificationsRef} />
    </Container>
  );
};

export default FileManager;